import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Table from "../../../../components/ReactTable/Table";
import { useTableData } from "../../../../components/ReactTable/useTableData";
import { PropTypes } from "prop-types";
import { useDispatch } from "react-redux";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../../../redux/actions/snackbarActions";
import { useTrainingLocations } from "../../../../hooks/trainingLocations";
import {
  statusTypes,
  courseStatuses,
  trainingLocationTypes
} from "../../../../constants/index";
import TextFilter from "../../../../components/ReactTable/Filters/TextFilter";
import StatusFilter from "./StatusFilter";
import { useLocations } from "../../../../hooks/locations";
import EditTrainingLocationModal from "./EditTrainingLocationModal";
import { getLocationString } from "../../../../utils/string";
import AddTrainingLocationButton from "./AddTrainingLocationButton";

const columns = t => [
  {
    Header: t("table.columnHeader.name"),
    accessor: "name",
    id: "name",
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll"),
    filterName: "nameCont"
  },
  {
    Header: t("table.columnHeader.location"),
    accessor: "location.city",
    Cell: ({
      row: {
        original: { city, state }
      }
    }) => getLocationString(city, state),
    id: "city"
  },
  {
    Header: t("table.columnHeader.type"),
    accessor: "locationType",
    Cell: ({
      row: {
        original: { location }
      }
    }) =>
      t("manageCourses.type", {
        context: !!location
          ? trainingLocationTypes.internal
          : trainingLocationTypes.external
      }),
    id: "locationId"
  },
  {
    Header: t("table.columnHeader.status"),
    accessor: "statusType.id",
    Cell: ({ cell: { value: status } }) => (
      <Typography variant="body2">
        {status === statusTypes.active
          ? t("manageCourses.coursesStatuses.active")
          : t("manageCourses.coursesStatuses.inactive")}
      </Typography>
    ),
    id: "statusTypeId"
  }
];

const TrainingLocations = () => {
  const dispatch = useDispatch();
  const {
    getTrainingLocations,
    updateTrainingLocation,
    isSaving
  } = useTrainingLocations();

  const { getLocations } = useLocations();

  const {
    data,
    isFetching,
    refreshData,
    paginationState,
    onStateChange,
    onApplyExternalFilters
  } = useTableData(getTrainingLocations, {
    externalFilters: { statusTypeIdEq: courseStatuses[1].id }
  });

  const { t } = useTranslation();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTrainingLocation, setSelectedTrainingLocation] = useState(
    null
  );

  const handleRowClick = row => {
    setSelectedTrainingLocation(data.find(({ id }) => id === row.id));
    setEditModalOpen(true);
  };

  const onEditTrainingLocation = values => {
    updateTrainingLocation(values)
      .then(() => {
        refreshData();
        getLocations();
        setEditModalOpen(false);
        const isInternal = !!selectedTrainingLocation.location;
        const statusWasMadeInactive =
          selectedTrainingLocation.statusType.id === statusTypes.active &&
          values.statusType.id === statusTypes.inactive;
        const successMessage =
          statusWasMadeInactive && isInternal
            ? t("manageCourses.trainingLocationMadeInactiveMsg")
            : t("manageCourses.updateTrainingLocationSuccessMsg");
        return dispatch(
          enqueueSuccessSnackbar({
            message: successMessage
          })
        );
      })
      .catch(() =>
        dispatch(
          enqueueErrorSnackbar({
            message: t("manageCourses.updateTrainingLocationErrorMsg")
          })
        )
      );
  };

  const handleFilter = newStatusFilter => {
    onApplyExternalFilters({ statusTypeIdEq: newStatusFilter });
  };

  const memoColumns = useMemo(() => columns(t), []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      mt={3}
      data-qa="virtual-classroom-table-container"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap-reverse"
        alignItems="center"
      >
        <Box mb={3}>
          <StatusFilter
            onChange={handleFilter}
            defaultValue={courseStatuses[1].id}
          />
        </Box>
        <Box mb={3}>
          <AddTrainingLocationButton onSave={() => refreshData()} />
        </Box>
      </Box>
      <Table
        data={data}
        columns={memoColumns}
        paginationState={paginationState}
        paginated
        onStateChange={onStateChange}
        onRowClick={handleRowClick}
        loading={isFetching}
        cellSize="medium"
      />
      <EditTrainingLocationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={onEditTrainingLocation}
        trainingLocation={selectedTrainingLocation}
        isSaving={isSaving}
      />
    </Box>
  );
};

export default TrainingLocations;

TrainingLocations.propTypes = {
  referenceType: PropTypes.oneOf(["location", "position", "group"]),
  referenceId: PropTypes.string
};
